/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { setClassToken } from '@amazon/vinyl-tsx'
import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { createDisposer, type Unsubscribe } from '@amazon/vinyl-util'
import { documentEvents } from '@/util/interaction'
import { setStageControlsVisible, STAGE_CONTROLS_HIDE_SECONDS } from '@/video/videoStage'

const idle$ = data(false)

/** True while the app root is full screen and its chrome is hidden for inactivity. */
export const fullscreenChromeIdle$: ObservableValue<boolean> = idle$

let revealBoundRoot: (() => void) | null = null

/** Counts as user activity: shows the full-screen chrome and restarts its idle dwell. */
export function revealFullscreenChrome(): void {
    revealBoundRoot?.()
}

/**
 * Auto-hides the chrome while `root` is full screen and the user is idle. The
 * activity surface is the whole root (header, transport, chat) plus any key
 * press, so keyboard users can always bring the chrome back.
 */
export function bindFullscreenChromeIdle(root: HTMLElement): Unsubscribe {
    const { add, dispose } = createDisposer()
    const isFullscreen = () => document.fullscreenElement === root
    let hideTimer: number | null = null
    let unbindActivity: Unsubscribe | null = null

    const clearHideTimer = () => {
        if (hideTimer != null) window.clearTimeout(hideTimer)
        hideTimer = null
    }
    const setChromeHidden = (hidden: boolean) => {
        setClassToken(root, 'idleHideChrome', hidden)
        // Drives the same controls-visible seam the video stage uses, so its
        // floating buttons fade in lock-step with the rest of the chrome.
        setStageControlsVisible(!hidden)
        idle$.value = hidden
    }
    const reveal = () => {
        setChromeHidden(false)
        clearHideTimer()
        hideTimer = window.setTimeout(() => {
            if (isFullscreen()) setChromeHidden(true)
        }, STAGE_CONTROLS_HIDE_SECONDS * 1000)
    }
    const bindActivity = (): Unsubscribe => {
        const activity = createDisposer()
        for (const type of ['mousemove', 'touchstart'] as const) {
            root.addEventListener(type, reveal)
            activity.add(() => root.removeEventListener(type, reveal))
        }
        // Capture phase so a Tab un-hides the chrome before the browser moves focus.
        activity.add(documentEvents.on('keydown', reveal, { capture: true }))
        return activity.dispose
    }
    const onFullscreenChange = () => {
        unbindActivity?.()
        unbindActivity = null
        clearHideTimer()
        if (isFullscreen()) {
            unbindActivity = bindActivity()
            // Enter clean: the user just interacted to go full screen.
            setChromeHidden(true)
        } else {
            setChromeHidden(false)
        }
    }

    const revealIfFullscreen = () => {
        if (isFullscreen()) reveal()
    }
    revealBoundRoot = revealIfFullscreen
    add(documentEvents.on('fullscreenchange', onFullscreenChange))
    add(() => {
        unbindActivity?.()
        clearHideTimer()
        idle$.value = false
        if (revealBoundRoot === revealIfFullscreen) revealBoundRoot = null
    })
    return dispose
}
